import { createApiWithDomain } from "./api.config";
import { ApiConstant, AppConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import QueryString from "qs";
import { toCamel, StorageUtil, convertString2JSON } from "utils";
import { getLabel } from "language";
import { CommonBranchInfoService } from "services";
import { BGlobalServerImage } from "const/image.const";
import store, { BranchActions } from "redux-store";
import { getCommonInteractor } from "services/local.service";
import { changeBranchServer } from "utils/branch.utils";
import { handlingLogoutBranch, refreshTokenData } from "utils/auth.utils";
import { isString } from "lodash";

// Only create RemoteApi api object when there are accountId/ branchId
export default class RemoteApi {
  constructor(prefixKey, domain, token) {
    if (!domain) throw new Error(`CAN NOT INIT API - Invalid domain - ${domain}`);

    this.prefixKey = prefixKey;
    this.api = createApiWithDomain(domain);
    this.configApi();
    this.setToken(token);
    this.isRefreshToken = false;
  }

  configApi = () => {
    this.api.addAsyncResponseTransform(async response => {
      if (response.status === ApiConstant.STT_UNAUTHORIZED && response.config.url !== ApiConstant.POST_REFRESH_TOKEN) {
        const newResponse = await this.handleRefreshToken(response);
        if (newResponse?.ok) {
          response.ok = newResponse.ok;
          response.status = newResponse.status;
          response.data = decryptClientData(response);
        }
      } else {
        response.data = decryptClientData(response);
      }
    });

    this.api.addAsyncRequestTransform(async request => {
      // Encrypt query params if present
      if (request.params && ApiConstant.TRIOS_API_VERSION > 2) {
        request.params = { params: encryptClientData(request.params) };
      }

      // Encrypt body if present
      if (request.data && ApiConstant.TRIOS_API_VERSION > 2) {
        request.data = QueryString.stringify({ body: encryptClientData(request.data) });
      }
    });
  };

  setToken = token => {
    if (token) {
      this.api.setHeader("Authorization", `Bearer ${token}`);
    }
  };

  handleLogout = async url => {
    await handlingLogoutBranch(this.prefixKey, url);
    const LocalDbManagement = getCommonInteractor().dbManagement;
    const activeBranchList = await LocalDbManagement.find({ state: SystemConstant.STATE.active });
    let dbManagementInfo = activeBranchList.find(item => false === url.includes(item.branch_domain));

    if (dbManagementInfo) {
      dbManagementInfo = toCamel(dbManagementInfo);
      const avatarUrl = CommonBranchInfoService.getBranchAvatarUrl(
        dbManagementInfo.branchDomain,
        dbManagementInfo.branchId,
      );
      const selectBranch = {
        id: dbManagementInfo.branchId,
        accountId: dbManagementInfo.accountId,
        loginState: dbManagementInfo.state,
        phone: dbManagementInfo.ownerName,
        name: dbManagementInfo.branchName,
        domain: dbManagementInfo.branchDomain,
        type:
          dbManagementInfo.branchId === SystemConstant.GLOBAL_BRANCH_ID
            ? SystemConstant.SERVER_TYPE.server
            : SystemConstant.SERVER_TYPE.branch,
        branchIcon: avatarUrl || BGlobalServerImage,
      };

      changeBranchServer(selectBranch);
      window.location.reload();
    }
  };

  handleRefreshToken = async response => {
    if (this.isRefreshToken) return;

    this.isRefreshToken = true;
    // Logout if more than number retry refresh token
    const requestRefreshToken = async (resp, retry = 1) => {
      if (retry > 3) {
        const currentDomain = window.electronUtils.storage.getCommonKey(KeyConstant.KEY_CURRENT_DOMAIN);
        const isCurrentBranch = this.api.getBaseURL().includes(currentDomain);
        if (isCurrentBranch) {
          window.dispatchEvent(
            new CustomEvent(AppConstant.NOTICE_EVENT_NAME, {
              detail: {
                content: getLabel(LangConstant.TXT_SESSION_EXPIRED),
                callback: () => this.handleLogout(this.api.getBaseURL()),
              },
            }),
          );
        } else {
          await handlingLogoutBranch(this.prefixKey, this.api.getBaseURL());
          store.dispatch(
            BranchActions.branchSet({
              fetchBranchTimestamp: new Date().getTime(),
            }),
          );
        }
        return resp;
      }

      const access_token = window.electronUtils.storage.getItem(KeyConstant.KEY_TOKEN, this.prefixKey);
      const refresh_token = window.electronUtils.storage.getItem(KeyConstant.KEY_REFRESH_TOKEN, this.prefixKey);

      const refreshResponse = await this.refreshToken({ access_token, refresh_token });

      let retryResponse = { ...resp };
      if (refreshResponse.status === ApiConstant.STT_OK) {
        const responseData = toCamel(refreshResponse.data);
        refreshTokenData(this.prefixKey, responseData);
        this.setToken(responseData.accessToken);

        const newApiConfig = { ...resp.config };
        newApiConfig.headers["Authorization"] = `Bearer ${responseData.accessToken}`;
        retryResponse = await this.api.any(newApiConfig); // Retry calling API after refresh token
      } else if (
        retryResponse.status !== ApiConstant.STT_MAINTAIN_1 ||
        retryResponse.status !== ApiConstant.STT_MAINTAIN_2 ||
        retryResponse.status !== ApiConstant.STT_MAINTAIN_3
      ) {
        retryResponse = await requestRefreshToken(resp, retry + 1);
      } else if (
        retry >= 3 &&
        (retryResponse.status === ApiConstant.STT_UNAUTHORIZED || retryResponse.status === ApiConstant.STT_FORBIDDEN)
      ) {
        await handlingLogoutBranch(this.prefixKey, retryResponse.config.url);
      }

      return retryResponse;
    };

    const retryResponse = await requestRefreshToken(response);
    this.isRefreshToken = false;

    return retryResponse;
  };

  refreshToken = data => this.api.post(ApiConstant.POST_REFRESH_TOKEN, QueryString.stringify(data));
}

const encryptClientData = data => {
  switch (ApiConstant.TRIOS_API_VERSION) {
    case 4:
      const prefixKey = StorageUtil.getCurrentPrefixKey();
      if (!prefixKey) {
        throw new Error(`decryptClientData - Invalid prefixKey - ${data}`);
      }

      return window.electronUtils.clientEncrypt.encryptClientData({
        data: isString(data) ? data : QueryString.stringify(data),
        deviceId: StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey),
      });

    case 3:
      return window.electronUtils.clientEncrypt.encryptClientData({
        data: isString(data) ? data : QueryString.stringify(data),
        deviceId: "",
      });

    default:
      return data;
  }
};

const decryptClientData = response => {
  let decryptData = response.data;
  let apiVersion = response.headers["trios-api-version"] ? parseInt(response.headers["trios-api-version"] ) : null
  switch (apiVersion) {
    case 4:
      const prefixKey = StorageUtil.getCurrentPrefixKey();
      if (!prefixKey) {
        throw new Error(`decryptClientData - Invalid prefixKey - ${response.data}`);
      }

      decryptData = window.electronUtils.clientEncrypt.decryptClientData({
        data: response.data,
        deviceId: StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey),
      });
      break;

    case 3:
      decryptData = window.electronUtils.clientEncrypt.decryptClientData({
        data: response.data,
        deviceId: "",
      });
      break;

    default:
      decryptData = response.data;
      break;
  }

  if (decryptData === response.data) {
    // No need decrypt
    return response.data;
  } else {
    const responseData = convertString2JSON(decryptData);
    return responseData?.data ? responseData.data : responseData;
  }
};
